import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Calendar,
  MapPin,
  Users,
  Truck,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Eye,
  FileDown,
  Search,
  Filter,
  BarChart3,
  CalendarDays,
  CalendarRange,
  TrendingUp,
  RefreshCw,
} from "lucide-react";
import { format, parseISO, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SynthesePlanningsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [unitaireEvents, setUnitaireEvents] = useState([]);
  const [multipleEvents, setMultipleEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Fetch all plannings
  const fetchPlannings = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [unitaireRes, multipleRes] = await Promise.all([
        axios.get(`${API}/api/events`, { headers }),
        axios.get(`${API}/api/organised-events`, { headers })
      ]);
      
      setUnitaireEvents(unitaireRes.data || []);
      setMultipleEvents(multipleRes.data || []);
    } catch (error) {
      console.error("Error fetching plannings:", error);
      toast.error("Erreur lors du chargement des plannings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchPlannings();
  }, [token]);

  // Combine and normalize all events
  const allEvents = useMemo(() => {
    const unitaire = unitaireEvents.map(e => ({
      ...e,
      type: "unitaire",
      displayDate: e.date_debut,
      displayStatus: e.statut || "en_attente",
      ftCount: e.participants?.length || 0,
      ftRequired: e.nombre_food_trucks_requis || 1,
    }));
    
    const multiple = multipleEvents.map(e => ({
      ...e,
      type: "multiple",
      displayDate: e.dates?.[0]?.date || "",
      displayStatus: e.statut || "brouillon",
      ftCount: e.food_trucks?.length || 0,
      ftRequired: e.nombre_food_trucks_requis || 1,
    }));
    
    return [...unitaire, ...multiple].sort((a, b) => 
      new Date(b.displayDate || b.created_at) - new Date(a.displayDate || a.created_at)
    );
  }, [unitaireEvents, multipleEvents]);

  // Filter events
  const filteredEvents = useMemo(() => {
    return allEvents.filter(event => {
      // Search filter
      const matchesSearch = !searchTerm || 
        event.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.lieu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.client?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Status filter
      const matchesStatus = statusFilter === "all" || event.displayStatus === statusFilter;
      
      // Type filter
      const matchesType = typeFilter === "all" || event.type === typeFilter;
      
      // Period filter
      let matchesPeriod = true;
      if (periodFilter !== "all" && event.displayDate) {
        const eventDate = parseISO(event.displayDate);
        const now = new Date();
        if (periodFilter === "this_month") {
          matchesPeriod = isWithinInterval(eventDate, {
            start: startOfMonth(now),
            end: endOfMonth(now)
          });
        } else if (periodFilter === "past") {
          matchesPeriod = eventDate < now;
        } else if (periodFilter === "future") {
          matchesPeriod = eventDate >= now;
        }
      }
      
      return matchesSearch && matchesStatus && matchesType && matchesPeriod;
    });
  }, [allEvents, searchTerm, statusFilter, typeFilter, periodFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = allEvents.length;
    const valides = allEvents.filter(e => ["valide", "confirme", "termine"].includes(e.displayStatus)).length;
    const enAttente = allEvents.filter(e => ["en_attente", "en_cours", "sondage_envoye", "en_attente_confirmation"].includes(e.displayStatus)).length;
    const brouillons = allEvents.filter(e => e.displayStatus === "brouillon").length;
    const annules = allEvents.filter(e => e.displayStatus === "annule").length;
    
    const unitaireCount = allEvents.filter(e => e.type === "unitaire").length;
    const multipleCount = allEvents.filter(e => e.type === "multiple").length;
    
    return { total, valides, enAttente, brouillons, annules, unitaireCount, multipleCount };
  }, [allEvents]);

  // Get status badge
  const getStatusBadge = (status) => {
    const config = {
      valide: { label: "Validé", variant: "default", className: "bg-green-600" },
      confirme: { label: "Confirmé", variant: "default", className: "bg-green-600" },
      termine: { label: "Terminé", variant: "default", className: "bg-gray-600" },
      en_attente: { label: "En attente", variant: "secondary", className: "" },
      en_cours: { label: "En cours", variant: "secondary", className: "" },
      sondage_envoye: { label: "Sondage envoyé", variant: "outline", className: "border-purple-500 text-purple-600" },
      en_attente_confirmation: { label: "Attente confirmation", variant: "outline", className: "border-amber-500 text-amber-600" },
      brouillon: { label: "Brouillon", variant: "outline", className: "" },
      annule: { label: "Annulé", variant: "destructive", className: "" },
    };
    const c = config[status] || { label: status, variant: "outline", className: "" };
    return <Badge variant={c.variant} className={c.className}>{c.label}</Badge>;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Type", "Titre", "Date", "Lieu", "Client", "Statut", "FT Requis", "FT Assignés", "Créé par", "Validé par"];
    const rows = filteredEvents.map(e => [
      e.type === "unitaire" ? "Unitaire" : "Multiple",
      e.titre || "",
      e.displayDate || "",
      e.lieu || "",
      e.client || "",
      e.displayStatus || "",
      e.ftRequired || "",
      e.ftCount || "",
      e.created_by_name || "",
      e.validated_by_name || ""
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `synthese_plannings_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    toast.success("Export CSV téléchargé");
  };

  // View event details
  const viewDetails = (event) => {
    setSelectedEvent(event);
    setShowDetailDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Synthèse des Plannings</h1>
          <p className="text-muted-foreground">Vue d'ensemble de tous les événements</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPlannings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button onClick={exportToCSV}>
            <FileDown className="w-4 h-4 mr-2" />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.valides}</p>
                <p className="text-xs text-muted-foreground">Validés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.enAttente}</p>
                <p className="text-xs text-muted-foreground">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-gray-50 to-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-600">{stats.brouillons}</p>
                <p className="text-xs text-muted-foreground">Brouillons</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.annules}</p>
                <p className="text-xs text-muted-foreground">Annulés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-600">{stats.unitaireCount}</p>
                <p className="text-xs text-muted-foreground">Unitaires</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CalendarRange className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{stats.multipleCount}</p>
                <p className="text-xs text-muted-foreground">Multiples</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par titre, lieu, client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="valide">Validé</SelectItem>
                <SelectItem value="confirme">Confirmé</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="sondage_envoye">Sondage envoyé</SelectItem>
                <SelectItem value="brouillon">Brouillon</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                <SelectItem value="unitaire">Unitaire</SelectItem>
                <SelectItem value="multiple">Multiple</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Période" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes périodes</SelectItem>
                <SelectItem value="this_month">Ce mois</SelectItem>
                <SelectItem value="future">À venir</SelectItem>
                <SelectItem value="past">Passés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        {filteredEvents.length} événement(s) trouvé(s)
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Lieu</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>FT</TableHead>
                <TableHead>Créé par</TableHead>
                <TableHead>Validé par</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                    Aucun événement trouvé
                  </TableCell>
                </TableRow>
              ) : (
                filteredEvents.map((event) => (
                  <TableRow key={event.id} className="hover:bg-gray-50">
                    <TableCell>
                      <Badge variant="outline" className={event.type === "unitaire" ? "border-indigo-300 text-indigo-600" : "border-purple-300 text-purple-600"}>
                        {event.type === "unitaire" ? (
                          <><CalendarDays className="w-3 h-3 mr-1" /> Unit.</>
                        ) : (
                          <><CalendarRange className="w-3 h-3 mr-1" /> Multi.</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {event.titre || "-"}
                    </TableCell>
                    <TableCell>
                      {event.displayDate ? format(parseISO(event.displayDate), "dd/MM/yyyy", { locale: fr }) : "-"}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        {event.lieu || "-"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {event.client || "-"}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(event.displayStatus)}
                    </TableCell>
                    <TableCell>
                      <span className={`flex items-center gap-1 ${event.ftCount >= event.ftRequired ? "text-green-600" : "text-amber-600"}`}>
                        <Truck className="w-3 h-3" />
                        {event.ftCount}/{event.ftRequired}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[120px] truncate">
                      {event.created_by_name || "-"}
                    </TableCell>
                    <TableCell className="text-sm max-w-[120px] truncate">
                      {event.validated_by_name ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {event.validated_by_name}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => viewDetails(event)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent?.type === "unitaire" ? (
                <CalendarDays className="w-5 h-5 text-indigo-600" />
              ) : (
                <CalendarRange className="w-5 h-5 text-purple-600" />
              )}
              {selectedEvent?.titre}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedEvent.displayStatus)}
                <Badge variant="outline">
                  {selectedEvent.type === "unitaire" ? "Planning Unitaire" : "Planning Multiple"}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {selectedEvent.displayDate 
                      ? format(parseISO(selectedEvent.displayDate), "dd MMMM yyyy", { locale: fr })
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Lieu</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {selectedEvent.lieu || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Client</p>
                  <p className="font-medium">{selectedEvent.client || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Food Trucks</p>
                  <p className="font-medium flex items-center gap-1">
                    <Truck className="w-4 h-4 text-gray-400" />
                    {selectedEvent.ftCount} / {selectedEvent.ftRequired} requis
                  </p>
                </div>
              </div>
              
              {selectedEvent.description && (
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p>{selectedEvent.description}</p>
                </div>
              )}
              
              <div className="border-t pt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Créé par</p>
                  <p>{selectedEvent.created_by_name || "-"}</p>
                  {selectedEvent.created_at && (
                    <p className="text-xs text-muted-foreground">
                      {format(parseISO(selectedEvent.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-muted-foreground">Validé par</p>
                  {selectedEvent.validated_by_name ? (
                    <>
                      <p className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {selectedEvent.validated_by_name}
                      </p>
                      {selectedEvent.validated_at && (
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(selectedEvent.validated_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-muted-foreground">Non validé</p>
                  )}
                </div>
              </div>
              
              {/* Food trucks list for multiple */}
              {selectedEvent.type === "multiple" && selectedEvent.food_trucks?.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-2">Food Trucks assignés</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedEvent.food_trucks.map((ft, i) => (
                      <Badge key={i} variant="secondary">
                        {ft.nom_food_truck || ft.food_truck_id}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Dates for multiple */}
              {selectedEvent.type === "multiple" && selectedEvent.dates?.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-2">Dates programmées</p>
                  <div className="space-y-1">
                    {selectedEvent.dates.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{format(parseISO(d.date), "dd/MM/yyyy", { locale: fr })}</span>
                        {d.services?.map((s, j) => (
                          <Badge key={j} variant="outline" className="text-xs">
                            {s.type === "midi" ? "🌞 Midi" : "🌙 Soir"}
                          </Badge>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
